import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useBeforeUnload, useLocation, useNavigate, useParams } from "react-router-dom";
import { isEqual } from "helpers";
import dayjs, { Dayjs } from "dayjs";
import { useSnackbar } from "notistack";
import StickyBox from "react-sticky-box";
import { FormikHelpers, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import PageDialog from "components/Dialogs/PageDialog";
import { PageDialogActions } from "components/Dialogs/PageDialogActions";
import {
  Alert,
  Box,
  Button,
  Card,
  DialogActions,
  DialogContent,
  Grid,
  IconButton,
  LinearProgress,
  Stack
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { ISportEquipmentDocuments } from "../dialogs";
import { useDialog } from "providers/DialogProvider";
import {
  EquipmentRepairRequestsDocument,
  GetSportEquipmentsDocument,
  ImageStoreEntity,
  SportEquipmentEntity,
  TechnicalCondition,
  UpdateSportEquipmentInput,
  useGetSportEquipmentByIdQuery,
  useSetEditableMutation,
  useUpdateSportEquipmentMutation
} from "graphql/graphQlApiHooks";
import { SportEquipmentAboutInfo } from "../views/SportEquipmentAboutInfo";
import { SportEquipmentLocation } from "../views/SportEquipmentLocation";
import { SportEquipmentCategory } from "../views/SportEquipmentCategory";
import { SportEquipmentPhotos } from "../views/SportEquipmentPhotos";
import { SportEquipmentDocuments } from "../views/SportEquipmentDocuments";
import { BarcodePhotoUpload } from "../views/BarcodePhotoUpload";
import { useGetSportEquipmentInputVars } from "store/reactiveVarsStores/spor-equipmnts/getSportEquipmentsInput";
import CommissioningDate from "../views/СommissioningDate";
import Iconify from "components/Iconify";
import { useReactiveVar } from "@apollo/client";
import { userVars } from "../../../../../../store/reactiveVarsStores/auth/user";
import { omit } from "lodash";
import { FormikErrors } from "formik/dist/types";
import ErrorsStepper from "../views/ErrorsStepper";
import { BootstrapTooltip } from "../../../../../../components/BootstrapTooltip";


interface ActionProps {
  isSubmitting: boolean;
  handleSubmit: () => void;
  errors: FormikErrors<UpdateSportEquipmentInput>;
  isValid: boolean;
  sportEquipment?: any;
  onClose: () => void;
}

const Actions = (props: ActionProps) => {
  const { isSubmitting, handleSubmit, errors, isValid, sportEquipment, onClose } = props;

  // Если редактирование запрещено, показываем кнопку "Закрыть" без логики сохранения
  if (!sportEquipment?.isEditable) {
    return (
      <>
        <PageDialogActions>
          <Box display={"flex"} flex={1} />
          <Button
            size='small'
            onClick={onClose}
            variant='contained'
          >
            Закрыть
          </Button>
        </PageDialogActions>
      </>
    );
  }

  // Проверяем условие: если статус "Не исправно", а failureReason пустой - кнопка должна быть disabled
  const isFailureReasonRequired = sportEquipment?.technicalCondition === TechnicalCondition.NotWorking;
  const isFailureReasonEmpty = !sportEquipment?.failureReason || sportEquipment?.failureReason.trim() === '';
  // Проверяем условие: если спортивный объект выбран, оборудование используется и отсутствует спортзона - кнопка должна быть disabled
  const isInUseWithoutSportZone = sportEquipment?.sportObject && sportEquipment?.inUse && (!sportEquipment?.sportZone || !sportEquipment?.sportSubZone);
  const isDisabled = isSubmitting || !isValid || (isFailureReasonRequired && isFailureReasonEmpty) || isInUseWithoutSportZone;

  return (
    <>
      <PageDialogActions>
        <ErrorsStepper errors={errors} />

        <Box
          display={"flex"}
          flex={1}
        />

        <LoadingButton
          loading={isSubmitting}
          disabled={isDisabled}
          size='small'
          onClick={() => handleSubmit()}
          variant='contained'
        >
          Сохранить
        </LoadingButton>
      </PageDialogActions>
    </>
  );
};

export const EditSportEquipment = () => {
  const { sportEquipmentId: id } = useParams();
  const { isForSportObject } = useReactiveVar(userVars);
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { getInput } = useGetSportEquipmentInputVars();
  const [dialog, closeDialog] = useDialog();

  const getBackPath = () => {
    if (location.pathname.startsWith('/repairs-request')) {
      return '/repairs-request';
    } else {
      return '/equipments';
    }
  };

  const [updateSportEquipment] = useUpdateSportEquipmentMutation();

  const [loading, setLoading] = useState(true);

  const [sportEquipment, setSportEquipment] = useState<SportEquipmentEntity | null>(null);
  const [sportEquipmentInit, setSportEquipmentInit] = useState<SportEquipmentEntity | null>(null);

  const UpdateSportEquipmentSchema = useMemo(() => Yup.object().shape({
    name: Yup.string()
      .required("Укажите название оборудования")
      .test('not-empty', 'Укажите название оборудования', (value) => {
        return value !== null && value !== undefined && value.trim() !== '';
      }),
    accountingName: Yup.string()
      .required("Укажите бухгалтерское название оборудования")
      .test('not-empty', 'Укажите бухгалтерское название оборудования', (value) => {
        return value !== null && value !== undefined && value.trim() !== '';
      }),
    inventoryNumber: Yup.string().required("Укажите инвентарный номер"),
    commissioningDate: Yup.string().required("Укажите дату ввода в эксплуатацию").nullable(),
    technicalCondition: Yup.string().required(),
    failureReason: Yup.string()
      .nullable()
      .when('technicalCondition', {
        is: TechnicalCondition.NotWorking,
        then: (schema) => schema
          .required("Укажите причину неисправности")
          .test('not-empty', 'Укажите причину неисправности', (value) => {
            return value !== null && value !== undefined && value.trim() !== '';
          }),
        otherwise: (schema) => schema
      }),

    sportObjectId: Yup.string().required("Выберите спортивный объект").nullable()
  }), []);


  const { data, error } = useGetSportEquipmentByIdQuery({ variables: { id }, skip: !id });

  if (error?.message) {
    enqueueSnackbar(error?.message, { variant: "error" });
  }

  // Создаем начальные значения для formik с помощью useMemo
  const initialValues = useMemo((): UpdateSportEquipmentInput => {
    if (sportEquipment) {
      return {
        ...(sportEquipment?.id && { id: sportEquipment.id }),
        name: sportEquipment?.name === null || sportEquipment?.name === undefined ? "" : sportEquipment.name,
        accountingName: sportEquipment?.accountingName === null || sportEquipment?.accountingName === undefined ? "" : sportEquipment.accountingName,
        inventoryNumber: sportEquipment?.inventoryNumber || "",
        description: sportEquipment?.description,
        length: sportEquipment?.length ?? null,
        width: sportEquipment?.width ?? null,
        height: sportEquipment?.height ?? null,
        weight: sportEquipment?.weight ?? null,
        inUse: sportEquipment?.inUse ?? true,
        availableForTransfer: sportEquipment?.availableForTransfer ?? false,
        isInformationComplete: sportEquipment?.isInformationComplete ?? false,
        technicalCondition: sportEquipment?.technicalCondition ?? TechnicalCondition.Working,
        failureReason: sportEquipment?.failureReason || "",
        commissioningDate: sportEquipment?.commissioningDate ? (dayjs(sportEquipment.commissioningDate).format("DD.MM.YYYY") === "Invalid Date" ? "" : dayjs(sportEquipment.commissioningDate).format("DD.MM.YYYY")) : "",
        sportCategoryId: sportEquipment?.sportCategoryObject?.id ? sportEquipment.sportCategoryObject.id : null,
        sportKindId: sportEquipment?.sportKindObject?.id ? sportEquipment.sportKindObject.id : null,
        equipmentTypeId: sportEquipment?.equipmentTypeObject?.id ? sportEquipment.equipmentTypeObject.id : null,
        sportObjectId: sportEquipment?.sportObject?.id || null,
        sportZoneId: sportEquipment?.sportZone?.id ? sportEquipment.sportZone.id : null,
        sportSubZoneId: sportEquipment?.sportSubZone?.id ? sportEquipment.sportSubZone.id : null,
        certificateIds: sportEquipment?.certificate?.length > 0 ? sportEquipment.certificate.map(item => item.id) : [],
        commissioningCertificateIds: sportEquipment?.commissioningCertificates?.length > 0 ? sportEquipment.commissioningCertificates.map(item => item.id) : [],
        guaranteeIds: sportEquipment?.guarantee?.length > 0 ? sportEquipment.guarantee.map(item => item.id) : [],
        manualIds: sportEquipment?.manual?.length > 0 ? sportEquipment.manual.map(item => item.id) : [],
        passportIds: sportEquipment?.passport?.length > 0 ? sportEquipment.passport.map(item => item.id) : [],
        otherDocumentsIds: sportEquipment?.otherDocuments?.length > 0 ? sportEquipment.otherDocuments.map(item => item.id) : [],
        mainPhotoId: (sportEquipment?.mainPhoto?.id) ? sportEquipment.mainPhoto.id : (sportEquipment?.photos?.length > 0 ? sportEquipment?.photos?.[0]?.id : undefined),
        photosIds: sportEquipment?.photos ? sportEquipment.photos.map(item => item.id) : [],
        barcodePhotoId: sportEquipment?.barcodePhoto?.id
      } as UpdateSportEquipmentInput;
    }
    // Возвращаем пустые значения, если данных еще нет
    return {
      name: "",
      accountingName: "",
      inventoryNumber: "",
      description: undefined,
      length: null,
      width: null,
      height: null,
      weight: null,
      inUse: true,
      availableForTransfer: false,
      isInformationComplete: false,
      technicalCondition: TechnicalCondition.Working,
      failureReason: "",
      commissioningDate: "",
      sportCategoryId: null,
      sportKindId: null,
      equipmentTypeId: null,
      sportObjectId: null,
      sportZoneId: null,
      sportSubZoneId: null,
      certificateIds: [],
      commissioningCertificateIds: [],
      guaranteeIds: [],
      manualIds: [],
      passportIds: [],
      otherDocumentsIds: [],
      mainPhotoId: undefined,
      photosIds: [],
      barcodePhotoId: undefined
    } as UpdateSportEquipmentInput;
  }, [sportEquipment]);

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: UpdateSportEquipmentSchema,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: false,
    enableReinitialize: false,
    onSubmit: (
      values: UpdateSportEquipmentInput | undefined,
      { setSubmitting, setErrors }: FormikHelpers<UpdateSportEquipmentInput>
    ) => {
      // Дополнительная проверка: если статус "Не исправно", а failureReason пустой - блокируем сохранение
      if (values?.technicalCondition === TechnicalCondition.NotWorking) {
        const failureReason = values?.failureReason;
        if (!failureReason || failureReason.trim() === '') {
          setErrors({ failureReason: 'Укажите причину неисправности' });
          setSubmitting(false);
          return;
        }
      }

      if (!isEqual(omit(sportEquipmentInit, ["isEditable"]), omit(sportEquipment, ["isEditable"]))) {
        updateSportEquipment({
          variables: {
            input: { ...values }
          },
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: GetSportEquipmentsDocument,
              fetchPolicy: "network-only",
              variables: {
                input: getInput()
              }
            }
          ]
        })
          .then((res) => {
            navigate(getBackPath());
            enqueueSnackbar(
              `Обрудование ${res?.data?.updateSportEquipment?.name}, успешно изменено.`
            );
          })
          .catch(({ message }) => {
            enqueueSnackbar(
              message.replace("Unexpected error value: ", "").replace(/\"/g, ""),
              {
                variant: "error"
              }
            );
          })
          .finally(() => setSubmitting(false));
      } else {
        dialog({
          title: "Закрыть диалог?",
          children: <>
            <DialogContent
              dividers
            >
              <Alert severity={"info"}>
                Данные не были изменены. Хотите закрыть редактирование?
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button
                size={"small"}
                onClick={() => {
                  navigate(getBackPath());
                  closeDialog();
                }}
                color={"inherit"}
                variant={"contained"}
              >
                Закрыть диалог
              </Button>
              <Button
                size={"small"}
                onClick={() => {
                  closeDialog();
                }}
                color={"primary"}
                autoFocus
                variant={"contained"}
              >
                Продолжить редактирование
              </Button>
            </DialogActions>
          </>
        });
        setSubmitting(false);
      }
    }
  });

  const { errors, touched, isSubmitting, handleSubmit, setValues, handleBlur, isValid, validateForm } = formik;

  // Обертка для setSportEquipment, которая также синхронизирует значения с formik
  const handleSetSportEquipment = useCallback((updater: React.SetStateAction<SportEquipmentEntity | null>) => {
    setSportEquipment((prevState) => {
      const newState = typeof updater === 'function' ? updater(prevState) : updater;
      
      // Синхронизируем критические поля с formik сразу при изменении
      if (newState) {
        setValues((prevValues) => {
          const updatedValues = {
            ...prevValues,
            ...(newState.name !== undefined && {
              name: newState.name === null ? "" : newState.name
            }),
            ...(newState.accountingName !== undefined && {
              accountingName: newState.accountingName === null ? "" : newState.accountingName
            }),
            ...(newState.inventoryNumber !== undefined && {
              inventoryNumber: newState.inventoryNumber || ""
            }),
            ...(newState.length !== undefined && {
              length: newState.length ?? null
            }),
            ...(newState.width !== undefined && {
              width: newState.width ?? null
            }),
            ...(newState.height !== undefined && {
              height: newState.height ?? null
            }),
            ...(newState.weight !== undefined && {
              weight: newState.weight ?? null
            }),
            ...(newState.commissioningDate !== undefined && {
              commissioningDate: newState.commissioningDate ? (dayjs(newState.commissioningDate).format("DD.MM.YYYY") === "Invalid Date" ? "" : dayjs(newState.commissioningDate).format("DD.MM.YYYY")) : ""
            }),
            ...(newState.sportObject !== undefined && {
              sportObjectId: newState.sportObject?.id || null
            }),
            ...(newState.sportZone !== undefined && {
              sportZoneId: newState.sportZone?.id || null
            }),
            ...(newState.sportSubZone !== undefined && {
              sportSubZoneId: newState.sportSubZone?.id || null
            }),
            ...(newState.technicalCondition !== undefined && {
              technicalCondition: newState.technicalCondition
            }),
            ...(newState.failureReason !== undefined && {
              failureReason: newState.failureReason || ""
            }),
            ...(newState.inUse !== undefined && {
              inUse: newState.inUse
            }),
            ...(newState.availableForTransfer !== undefined && {
              availableForTransfer: newState.availableForTransfer
            }),
            ...(newState.isInformationComplete !== undefined && {
              isInformationComplete: newState.isInformationComplete
            })
          };
          
          // Если изменилось техническое состояние, перевалидируем форму
          if (prevState && newState.technicalCondition !== prevState.technicalCondition) {
            // Используем setTimeout чтобы дать formik обновить значения перед валидацией
            setTimeout(() => {
              validateForm();
            }, 0);
          }
          
          return updatedValues;
        });
      }
      
      return newState;
    });
  }, [setValues, validateForm]);

  const onClose = () => {
    // Если редактирование запрещено, просто закрываем диалог без проверки на изменения
    if (!sportEquipment?.isEditable) {
      navigate(getBackPath());
      return;
    }

    if (!isEqual(omit(sportEquipmentInit, ["isEditable"]), omit(sportEquipment, ["isEditable"]))) {
      dialog({
        title: "Данные будут утеряны",
        children: <>
          <DialogContent
            dividers
          >
            <Alert severity={"warning"}>
              Вы уверены что хотите закрыть диалог редактирования оборудования?
              Изменённые данные будут утерены!
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button
              size={"small"}
              onClick={() => {
                navigate(getBackPath());
                closeDialog();
              }}
              color={"inherit"}
              variant={"contained"}
            >
              Закрыть диалог, с потерей данных
            </Button>
            <Button
              size={"small"}
              onClick={() => {
                closeDialog();
              }}
              color={"primary"}
              autoFocus
              variant={"contained"}
            >
              Продолжить редактирование
            </Button>
          </DialogActions>
        </>
      });
    } else {
      navigate(getBackPath());
    }
  };

  // получаем данные оборудования по id и устанавливаем их в стейт (sportEquipment)
  useEffect(() => {
    if (data?.getSportEquipmentById) {
      let getSportEquipmentById = data?.getSportEquipmentById as SportEquipmentEntity;
      // получаем дату введения в эксплуатацию
      const commissioningDate = getSportEquipmentById?.commissioningDate;

      // getSportEquipmentById = omit(getSportEquipmentById, ["isEditable"]);

      // если нет главной фотографии, но есть фотографии, то берём первую фотографию
      if (!getSportEquipmentById?.mainPhoto && getSportEquipmentById?.photos?.length > 0)
        getSportEquipmentById = { ...getSportEquipmentById, mainPhoto: getSportEquipmentById?.photos?.[0] };

      // если  дата введения в эксплуатацию в числовом формате, то преобразуем её в строку
      if (!isNaN(Number(commissioningDate)))
        getSportEquipmentById = {
          ...getSportEquipmentById,
          commissioningDate: dayjs(Number(commissioningDate)).format("YYYY-MM-DD")
        };

      // если это первая згрузка, то устанавливаем значения в стейты для сравнения с изменёнными данными
      if (getSportEquipmentById && !sportEquipment) {
        setSportEquipment(getSportEquipmentById);
        setSportEquipmentInit(getSportEquipmentById);
      }
    }
  }, [data?.getSportEquipmentById]);

  // Устанавливаем данные оборудования в formik при первой загрузке
  const [isFormikInitialized, setIsFormikInitialized] = useState(false);

  // устанавливаем данные оборудования из стейта в форму редактирования оборудования (formik) при загрузке страницы
  useEffect(() => {
    // Устанавливаем значения только один раз при первой загрузке данных
    if (sportEquipment && !isFormikInitialized && sportEquipment.id) {
      // Используем initialValues напрямую для установки всех значений сразу
      setValues(initialValues);
      setIsFormikInitialized(true);
      if (loading) setLoading(false);
    } else if (sportEquipment && loading) {
      setLoading(false);
    }
  }, [sportEquipment, isFormikInitialized, initialValues, setValues, loading]);


  // хук для изменения доступности редактирования карточки оборудования
  const [setEditable] = useSetEditableMutation();

  // обработчик изменения доступности редактирования карточки оборудования
  const handleEquipmentEditable = () => {
    // отправляем запрос на изменение доступности редактирования карточки оборудования
    setEditable({
      variables: {
        input: {
          sportEquipmentId: id,
          isEditable: !sportEquipment?.isEditable // меняем флаг доступности редактирования
        }
      },
      // обновляем кэш запроса GetSportEquipmentsDocument
      refetchQueries: [
        {
          query: GetSportEquipmentsDocument,
          fetchPolicy: "network-only",
          variables: {
            input: getInput()
          }
        },
        {
          query: EquipmentRepairRequestsDocument,
          fetchPolicy: "network-only",
        }
      ]
    }).then(() => {
      // меняем флаг доступности редактирования
      setSportEquipment(prev => ({
        ...prev,
        isEditable: !sportEquipment?.isEditable
      }));
      // выводим сообщение в snackbar
      enqueueSnackbar(
        sportEquipment?.isEditable ? `Карточка оборудования больше не доступна для редактирования.` : "Карточка оборудования вновь доступна для редактирования."
      );
    }).catch(({ message }) => {
      // выводим сообщение об ошибке в snackbar
      enqueueSnackbar(
        message.replace("Unexpected error value: ", "").replace(/\"/g, ""),
        {
          variant: "error"
        }
      );
    });
  };


  useBeforeUnload(
    React.useCallback((event) => {
      if (!isEqual(omit(sportEquipmentInit, ["isEditable"]), omit(sportEquipment, ["isEditable"]))) {
        event.preventDefault();
        event.returnValue = "Вы уверены, что хотите покинуть страницу? Все несохраненные данные будут потеряны.";
      }
    }, [sportEquipment, sportEquipmentInit])
  );

  return (
    <>
      <PageDialog
        title={sportEquipment?.name === "" ? "" : sportEquipment?.name}
        onClose={onClose}
        maxWidth={"lg"}
        ActionsComponent={Actions}
        ActionsProps={{
          isSubmitting: isSubmitting,
          handleSubmit: () => handleSubmit(),
          errors: errors,
          isValid: isValid,
          sportEquipment: sportEquipment,
          onClose: onClose
        }}
        additionalButtonComponents={[
          <Box key={1}>
            {!isForSportObject && !loading &&

              <BootstrapTooltip title={sportEquipment?.isEditable ? "Запретить редактирование" : "Разрешить редактирование"}>
                <IconButton
                  size={"small"}
                  onClick={handleEquipmentEditable}
                >
                  <Iconify
                    icon={sportEquipment?.isEditable ? "material-symbols:lock-open-outline" : "material-symbols:lock"}
                    fontSize={"inherit"}
                  />
                </IconButton>
              </BootstrapTooltip>

            }
          </Box>,
          <Box key={2}>
            {!loading &&
              <BootstrapTooltip title={"История изменений"}>
                <IconButton
                  size={"small"}
                  onClick={() => {
                    navigate('/equipments/history/'+id)
                  }}
                >
                  <Iconify
                    icon={"material-symbols:history-rounded"}
                    fontSize={"inherit"}
                  />
                </IconButton>
              </BootstrapTooltip>
            }
          </Box>
        ]}
      >


        {loading ? <LinearProgress /> :

          <FormikProvider value={formik}>

            <Grid
              container
              spacing={4}
              px={3}
              pb={3}
            >

              <Grid
                item
                lg={2.5}
                md={3.5}
              >
                <StickyBox
                  offsetTop={11}
                  offsetBottom={11}
                >
                  <Stack
                    spacing={2}
                    mt={2}
                  >

                    <Card
                      sx={{ p: 2 }}
                      elevation={2}
                    >
                      <CommissioningDate
                        touched={touched}
                        errors={errors}
                        isEditable={sportEquipment?.isEditable}
                        handleBlur={handleBlur}

                        date={dayjs(sportEquipment?.commissioningDate)}
                        setDate={(date: Dayjs | null) => {
                          setSportEquipment(prevState => ({
                              ...prevState,
                              commissioningDate: dayjs(date).format("YYYY-MM-DD")
                            })
                          );
                        }}
                      />
                    </Card>

                    <Card
                      sx={{ p: 2 }}
                      elevation={2}
                    >
                      <SportEquipmentLocation
                        vertical
                        isEditable={sportEquipment?.isEditable}
                        handleBlur={handleBlur}
                        touched={touched}
                        errors={errors}
                        location={{
                          sportObject: sportEquipment?.sportObject,
                          sportZone: sportEquipment?.sportZone,
                          sportSubZone: sportEquipment?.sportSubZone
                        }}
                        onChangeLocation={(sportObject, sportZone, sportSubZone) => {
                          setSportEquipment(prevState => ({
                            ...prevState,
                            sportObject,
                            sportZone,
                            sportSubZone
                          }));
                          // Синхронизируем значения локации с formik
                          setValues(prevState => ({
                            ...prevState,
                            sportObjectId: sportObject?.id || null,
                            sportZoneId: sportZone?.id || null,
                            sportSubZoneId: sportSubZone?.id || null
                          }));
                        }}
                      />
                      {/* Предупреждение: используемое оборудование нельзя разместить на складе */}
                      {sportEquipment?.sportObject && sportEquipment?.inUse && (!sportEquipment?.sportZone || !sportEquipment?.sportSubZone) && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                          Используемое оборудование нельзя разместить на складе. Укажите спортзону или снимите оборудование с использования.
                        </Alert>
                      )}
                    </Card>

                    <Card
                      sx={{ p: 2 }}
                      elevation={2}
                    >
                      <SportEquipmentCategory
                        vertical
                        touched={touched}
                        handleBlur={handleBlur}
                        errors={errors}
                        isEditable={sportEquipment?.isEditable}
                        sportEquipment={sportEquipment}
                        setSportEquipment={setSportEquipment}
                      />
                    </Card>
                  </Stack>
                </StickyBox>
              </Grid>

              <Grid
                item
                lg={12 - 2.5}
                md={12 - 3.5}
                sx={{
                  transition: "all .3s ease-in-out"
                }}
              >
                <Stack
                  spacing={4}
                  mt={2}
                >
                  <SportEquipmentAboutInfo
                    errors={errors}
                    sportEquipment={sportEquipment}
                    setSportEquipment={handleSetSportEquipment}
                    isEditable={sportEquipment?.isEditable}
                    handleBlur={handleBlur}
                    touched={touched}
                  />

                  <Card
                    sx={{ p: 3 }}
                    elevation={2}
                  >
                    <SportEquipmentPhotos
                      isEditable={sportEquipment?.isEditable}
                      sportEquipmentPhotos={sportEquipment?.photos || []}
                      setSportEquipmentPhotos={(photos) => {
                        setSportEquipment(prevState => ({
                          ...prevState,
                          photos: photos as ImageStoreEntity[]
                        }));
                        // Обновляем photosIds в formik при изменении photos
                        setValues(prevState => ({
                          ...prevState,
                          photosIds: photos?.map(photo => photo.id) || []
                        }));
                      }}
                      mainPhoto={sportEquipment?.mainPhoto}
                      setMainPhoto={(mainPhoto) => {
                        setSportEquipment(prevState => ({
                          ...prevState,
                          mainPhoto: mainPhoto as ImageStoreEntity
                        }));
                        // Обновляем mainPhotoId в formik при изменении mainPhoto
                        setValues(prevState => ({
                          ...prevState,
                          mainPhotoId: mainPhoto?.id || undefined
                        }));
                      }}
                    />
                  </Card>

                  <Card
                    sx={{ p: 3 }}
                    elevation={2}
                  >
                    <BarcodePhotoUpload
                      isEditable={sportEquipment?.isEditable}
                      barcodePhoto={sportEquipment?.barcodePhoto}
                      setBarcodePhoto={(photo) => {
                        setSportEquipment(prevState => ({
                          ...prevState,
                          barcodePhoto: photo as ImageStoreEntity
                        }));
                        // Обновляем barcodePhotoId в formik при изменении barcodePhoto
                        setValues(prevState => ({
                          ...prevState,
                          barcodePhotoId: photo?.id || undefined
                        }));
                      }}
                    />
                  </Card>

                  <SportEquipmentDocuments
                    isEditable={sportEquipment?.isEditable}
                    documents={{
                      ...(sportEquipment?.certificate?.length > 0 && { certificate: sportEquipment?.certificate }),
                      ...(sportEquipment?.commissioningCertificates?.length > 0 && { commissioningCertificate: sportEquipment?.commissioningCertificates }),
                      ...(sportEquipment?.guarantee?.length > 0 && { guarantee: sportEquipment?.guarantee }),
                      ...(sportEquipment?.manual?.length > 0 && { manual: sportEquipment?.manual }),
                      ...(sportEquipment?.passport?.length > 0 && { passport: sportEquipment?.passport }),
                      ...(sportEquipment?.otherDocuments?.length > 0 && { otherDocuments: sportEquipment?.otherDocuments })
                    } as ISportEquipmentDocuments}
                    setDocuments={(documents: ISportEquipmentDocuments) => {
                      setSportEquipment(prevState => ({
                        ...prevState,
                        certificate: documents?.certificate,
                        commissioningCertificates: documents?.commissioningCertificate,
                        guarantee: documents?.guarantee,
                        passport: documents?.passport,
                        manual: documents?.manual,
                        otherDocuments: documents?.otherDocuments
                      }));
                    }}
                  />
                </Stack>
              </Grid>

            </Grid>


          </FormikProvider>
        }


      </PageDialog>


    </>
  );
};
